/**
 * Todo Dependency Service
 * Handles task dependencies and blocking relationships
 * Part of the modularized todo service architecture
 */

import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from '../service-registry';
import { typedSupabase } from '@/lib/typed-supabase';
import { Todo } from '@/types/todo';
import { logger } from '@/lib/logger';

export interface TaskDependency {
  id: string;
  task_id: string;
  depends_on_task_id: string;
  dependency_type: 'blocks' | 'blocked_by';
  created_at: string;
  updated_at: string;
}

type TaskDependencyRow = {
  id: string;
  predecessor_task_id: string;
  successor_task_id: string;
  dependency_type: string;
  created_at: string | null;
  updated_at: string | null;
};

export interface ITodoDependencyService extends IService {
  addDependency(taskId: string, dependsOnTaskId: string): Promise<ServiceResponse<TaskDependency>>;
  removeDependency(taskId: string, dependsOnTaskId: string): Promise<ServiceResponse<void>>;
  getBlockingTasks(taskId: string): Promise<ServiceResponse<Todo[]>>;
  getDependentTasks(taskId: string): Promise<ServiceResponse<Todo[]>>;
  getAllDependencies(taskId: string): Promise<ServiceResponse<TaskDependency[]>>;
  validateDependencyChain(taskId: string, dependsOnTaskId: string): Promise<ServiceResponse<boolean>>;
  getTaskHierarchy(taskId: string): Promise<ServiceResponse<{ ancestors: Todo[]; descendants: Todo[] }>>;
}

export class TodoDependencyService implements ITodoDependencyService {
  readonly name = 'TodoDependencyService';
  private initialized = false;

  private mapDependencyRow(row: TaskDependencyRow): TaskDependency {
    return {
      id: row.id,
      task_id: row.successor_task_id,
      depends_on_task_id: row.predecessor_task_id,
      dependency_type: row.dependency_type === 'blocked_by' ? 'blocked_by' : 'blocks',
      created_at: row.created_at ?? new Date().toISOString(),
      updated_at: row.updated_at ?? new Date().toISOString(),
    };
  }

  async initialize(): Promise<void> {
    this.initialized = true;
    logger.info('TodoDependencyService initialized');
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info('TodoDependencyService disposed');
  }

  async addDependency(taskId: string, dependsOnTaskId: string): Promise<ServiceResponse<TaskDependency>> {
    try {
      logger.debug('Adding dependency', { taskId, dependsOnTaskId });

      // Validate that both tasks exist
      const taskExists = await this.validateTasksExist([taskId, dependsOnTaskId]);
      if (!taskExists) {
        return createErrorResponse('One or more tasks not found');
      }

      // Check for circular dependencies
      const isValidChain = await this.validateDependencyChain(taskId, dependsOnTaskId);
      if (!isValidChain.success || !isValidChain.data) {
        return createErrorResponse('Circular dependency detected');
      }

      // Create the dependency relationship
      const { data, error } = await typedSupabase
        .from('task_dependencies')
        .insert({
          predecessor_task_id: dependsOnTaskId,
          successor_task_id: taskId,
          dependency_type: 'finish_to_start',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          logger.warn('Dependency already exists', { taskId, dependsOnTaskId });
          return createErrorResponse('Dependency already exists');
        }
        logger.error('Failed to add dependency', { error: error.message, taskId, dependsOnTaskId });
        throw error;
      }

      logger.info('Dependency added successfully', { taskId, dependsOnTaskId, dependencyId: data.id });
      return createSuccessResponse(this.mapDependencyRow(data as TaskDependencyRow));

    } catch (error) {
      logger.error('Error in addDependency', {
        error: error instanceof Error ? error.message : error,
        taskId,
        dependsOnTaskId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Add dependency failed');
    }
  }

  async removeDependency(taskId: string, dependsOnTaskId: string): Promise<ServiceResponse<void>> {
    try {
      logger.debug('Removing dependency', { taskId, dependsOnTaskId });

      const { error } = await typedSupabase
        .from('task_dependencies')
        .delete()
        .eq('successor_task_id', taskId)
        .eq('predecessor_task_id', dependsOnTaskId);

      if (error) {
        logger.error('Failed to remove dependency', { error: error.message, taskId, dependsOnTaskId });
        throw error;
      }

      logger.info('Dependency removed successfully', { taskId, dependsOnTaskId });
      return createSuccessResponse(undefined);

    } catch (error) {
      logger.error('Error in removeDependency', {
        error: error instanceof Error ? error.message : error,
        taskId,
        dependsOnTaskId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Remove dependency failed');
    }
  }

  async getBlockingTasks(taskId: string): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting blocking tasks', { taskId });

      // Get tasks that this task depends on (blocking tasks)
      const { data: dependencies, error: depError } = await typedSupabase
        .from('task_dependencies')
        .select('predecessor_task_id')
        .eq('successor_task_id', taskId);

      if (depError) {
        logger.error('Failed to get dependencies', { error: depError.message, taskId });
        throw depError;
      }

      if (!dependencies || dependencies.length === 0) {
        return createSuccessResponse([]);
      }

      const blockingTaskIds = dependencies.map(d => d.predecessor_task_id);

      const { data: tasks, error: taskError } = await typedSupabase
        .from('todos')
        .select('*')
        .in('id', blockingTaskIds);

      if (taskError) {
        logger.error('Failed to get blocking tasks', { error: taskError.message, taskId });
        throw taskError;
      }

      logger.debug('Blocking tasks retrieved', { taskId, count: tasks.length });
      return createSuccessResponse((tasks ?? []) as unknown as Todo[]);

    } catch (error) {
      logger.error('Error in getBlockingTasks', {
        error: error instanceof Error ? error.message : error,
        taskId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Get blocking tasks failed');
    }
  }

  async getDependentTasks(taskId: string): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting dependent tasks', { taskId });

      // Get tasks that depend on this task
      const { data: dependencies, error: depError } = await typedSupabase
        .from('task_dependencies')
        .select('successor_task_id')
        .eq('predecessor_task_id', taskId);

      if (depError) {
        logger.error('Failed to get dependent dependencies', { error: depError.message, taskId });
        throw depError;
      }

      if (!dependencies || dependencies.length === 0) {
        return createSuccessResponse([]);
      }

      const dependentTaskIds = dependencies.map(d => d.successor_task_id);

      const { data: tasks, error: taskError } = await typedSupabase
        .from('todos')
        .select('*')
        .in('id', dependentTaskIds);

      if (taskError) {
        logger.error('Failed to get dependent tasks', { error: taskError.message, taskId });
        throw taskError;
      }

      logger.debug('Dependent tasks retrieved', { taskId, count: tasks.length });
      return createSuccessResponse(tasks as Todo[]);

    } catch (error) {
      logger.error('Error in getDependentTasks', {
        error: error instanceof Error ? error.message : error,
        taskId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Get dependent tasks failed');
    }
  }

  async getAllDependencies(taskId: string): Promise<ServiceResponse<TaskDependency[]>> {
    try {
      logger.debug('Getting all dependencies', { taskId });

      // Get both incoming and outgoing dependencies
      const { data, error } = await typedSupabase
        .from('task_dependencies')
        .select('*')
        .or(`successor_task_id.eq.${taskId},predecessor_task_id.eq.${taskId}`)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Failed to get all dependencies', { error: error.message, taskId });
        throw error;
      }

      logger.debug('All dependencies retrieved', { taskId, count: data.length });
      return createSuccessResponse(((data ?? []) as TaskDependencyRow[]).map((row) => this.mapDependencyRow(row)));

    } catch (error) {
      logger.error('Error in getAllDependencies', {
        error: error instanceof Error ? error.message : error,
        taskId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Get all dependencies failed');
    }
  }

  async validateDependencyChain(taskId: string, dependsOnTaskId: string): Promise<ServiceResponse<boolean>> {
    try {
      logger.debug('Validating dependency chain', { taskId, dependsOnTaskId });

      // Prevent self-dependency
      if (taskId === dependsOnTaskId) {
        return createSuccessResponse(false);
      }

      // Check if creating this dependency would create a cycle
      const visited = new Set<string>();
      const isCyclic = await this.detectCycle(dependsOnTaskId, taskId, visited);

      const isValid = !isCyclic;
      logger.debug('Dependency chain validation result', { taskId, dependsOnTaskId, isValid });

      return createSuccessResponse(isValid);

    } catch (error) {
      logger.error('Error in validateDependencyChain', {
        error: error instanceof Error ? error.message : error,
        taskId,
        dependsOnTaskId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Validate dependency chain failed');
    }
  }

  async getTaskHierarchy(taskId: string): Promise<ServiceResponse<{ ancestors: Todo[]; descendants: Todo[] }>> {
    try {
      logger.debug('Getting task hierarchy', { taskId });

      // Get all ancestors (tasks this task depends on, recursively)
      const ancestors = await this.getAncestorsRecursive(taskId);

      // Get all descendants (tasks that depend on this task, recursively)
      const descendants = await this.getDescendantsRecursive(taskId);

      logger.debug('Task hierarchy retrieved', {
        taskId,
        ancestorsCount: ancestors.length,
        descendantsCount: descendants.length
      });

      return createSuccessResponse({ ancestors, descendants });

    } catch (error) {
      logger.error('Error in getTaskHierarchy', {
        error: error instanceof Error ? error.message : error,
        taskId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Get task hierarchy failed');
    }
  }

  // Private helper methods

  private async validateTasksExist(taskIds: string[]): Promise<boolean> {
    const { data, error } = await typedSupabase
      .from('todos')
      .select('id')
      .in('id', taskIds);

    if (error) {
      logger.error('Failed to validate task existence', { error: error.message, taskIds });
      return false;
    }

    return data?.length === taskIds.length;
  }

  private async detectCycle(startTaskId: string, targetTaskId: string, visited: Set<string>): Promise<boolean> {
    if (visited.has(startTaskId)) {
      return false; // Already visited this path
    }

    visited.add(startTaskId);

    // If we reach the target, we have a cycle
    if (startTaskId === targetTaskId) {
      return true;
    }

    // Get tasks that this task depends on
    const { data: dependencies } = await typedSupabase
      .from('task_dependencies')
      .select('predecessor_task_id')
      .eq('successor_task_id', startTaskId);

    if (!dependencies) {
      return false;
    }

    // Recursively check each dependency
    for (const dep of dependencies) {
      const hasCycle = await this.detectCycle(dep.predecessor_task_id, targetTaskId, new Set(visited));
      if (hasCycle) {
        return true;
      }
    }

    return false;
  }

  private async getAncestorsRecursive(taskId: string, visited: Set<string> = new Set()): Promise<Todo[]> {
    if (visited.has(taskId)) {
      return [];
    }
    visited.add(taskId);

    const { data: dependencies } = await typedSupabase
      .from('task_dependencies')
      .select('predecessor_task_id')
      .eq('successor_task_id', taskId);

    if (!dependencies || dependencies.length === 0) {
      return [];
    }

    const ancestorIds = dependencies.map(d => d.predecessor_task_id);
    const { data: ancestors } = await typedSupabase
      .from('todos')
      .select('*')
      .in('id', ancestorIds);

    if (!ancestors) {
      return [];
    }

    // Recursively get ancestors of ancestors
    const allAncestors: Todo[] = [...((ancestors ?? []) as unknown as Todo[])];
    for (const ancestor of ancestors) {
      const deeperAncestors = await this.getAncestorsRecursive(ancestor.id, new Set(visited));
      allAncestors.push(...deeperAncestors);
    }

    return allAncestors;
  }

  private async getDescendantsRecursive(taskId: string, visited: Set<string> = new Set()): Promise<Todo[]> {
    if (visited.has(taskId)) {
      return [];
    }
    visited.add(taskId);

    const { data: dependencies } = await typedSupabase
      .from('task_dependencies')
      .select('successor_task_id')
      .eq('predecessor_task_id', taskId);

    if (!dependencies || dependencies.length === 0) {
      return [];
    }

    const descendantIds = dependencies.map(d => d.successor_task_id);
    const { data: descendants } = await typedSupabase
      .from('todos')
      .select('*')
      .in('id', descendantIds);

    if (!descendants) {
      return [];
    }

    // Recursively get descendants of descendants
    const allDescendants: Todo[] = [...((descendants ?? []) as unknown as Todo[])];
    for (const descendant of descendants) {
      const deeperDescendants = await this.getDescendantsRecursive(descendant.id, new Set(visited));
      allDescendants.push(...deeperDescendants);
    }

    return allDescendants;
  }
}

// Export singleton instance
export const todoDependencyService = new TodoDependencyService();
